import { apiClient } from './client';
import type { components } from './schema.gen';

export type Partner = components['schemas']['PartnerView'];
export type ApiKey = components['schemas']['ApiKeyView'];
export type IssuedKey = components['schemas']['IssuedKeyView'];

export type PartnerStatus = 'ACTIVE' | 'SUSPENDED' | 'ARCHIVED';

export type CreatePartnerInput = {
  name: string;
  description?: string;
  contactName?: string;
  contactEmail?: string;
  contactPhone?: string;
  allowedAttributeTypes?: string[];
  rateLimitPerMinute?: number;
  dailyQuota?: number;
  notes?: string;
};

export type UpdatePartnerInput = Partial<CreatePartnerInput>;

export type ListPartnerFilters = {
  status?: PartnerStatus;
  search?: string;
  limit?: number;
  offset?: number;
};

export async function listPartners(filters: ListPartnerFilters = {}): Promise<{
  items: Partner[];
  total: number;
}> {
  const { data, error } = await apiClient.GET('/v1/partners', {
    params: {
      query: {
        status: filters.status ?? '',
        search: filters.search ?? '',
        limit: filters.limit ?? 50,
        offset: filters.offset ?? 0,
      },
    },
  });
  if (error || !data) return { items: [], total: 0 };
  return { items: (data.items ?? []) as Partner[], total: data.total ?? 0 };
}

export async function getPartner(id: number): Promise<Partner | null> {
  const { data, error } = await apiClient.GET('/v1/partners/{id}', {
    params: { path: { id } },
  });
  if (error || !data) return null;
  return data as Partner;
}

export async function createPartner(input: CreatePartnerInput): Promise<Partner | null> {
  const { data, error } = await apiClient.POST('/v1/partners', { body: input });
  if (error || !data) return null;
  return data as Partner;
}

export async function updatePartner(id: number, input: UpdatePartnerInput): Promise<Partner | null> {
  const { data, error } = await apiClient.PATCH('/v1/partners/{id}', {
    params: { path: { id } },
    body: input,
  });
  if (error || !data) return null;
  return data as Partner;
}

export async function archivePartner(id: number): Promise<boolean> {
  const { error } = await apiClient.DELETE('/v1/partners/{id}', {
    params: { path: { id } },
  });
  return !error;
}

// ---------------------------------------------------------------------------
// API keys
// ---------------------------------------------------------------------------

export async function listPartnerKeys(partnerId: number): Promise<ApiKey[]> {
  const { data, error } = await apiClient.GET('/v1/partners/{id}/keys', {
    params: { path: { id: partnerId } },
  });
  if (error || !data) return [];
  return (data.items ?? []) as ApiKey[];
}

/**
 * Origines qu'une clé peut porter. Vide = nos équipes : une application qui
 * pousse ses événements avec une clé MAFA enrôle pour MAFA.
 */
export type KeyRecruitmentSource = '' | 'ORANGE_PARTENAIRE' | 'ID30' | 'MAFA_TERRAIN';

export type CreateKeyInput = {
  label: string;
  env?: 'live' | 'test';
  expiresAt?: string;
  recruitmentSource?: KeyRecruitmentSource;
  recruitmentPartner?: string;
};

export async function createPartnerKey(
  partnerId: number,
  input: CreateKeyInput,
): Promise<IssuedKey | null> {
  const { data, error } = await apiClient.POST('/v1/partners/{id}/keys', {
    params: { path: { id: partnerId } },
    body: input,
  });
  if (error || !data) return null;
  return data as IssuedKey;
}

export async function revokePartnerKey(
  partnerId: number,
  keyId: number,
  reason: string,
): Promise<boolean> {
  const { error } = await apiClient.DELETE('/v1/partners/{partnerId}/keys/{keyId}', {
    params: { path: { partnerId, keyId } },
    body: { reason },
  });
  return !error;
}

/**
 * Change l'origine portée par une clé existante.
 *
 * Ne vaut que pour l'avenir : l'origine se pose à la création d'un dossier et
 * n'est jamais rejouée. Corriger une clé mal configurée ne réattribue donc pas
 * les dossiers qu'elle a déjà créés.
 */
export async function updateKeyRecruitmentOrigin(
  partnerId: number,
  keyId: number,
  input: { recruitmentSource: KeyRecruitmentSource; recruitmentPartner?: string },
): Promise<ApiKey | null> {
  const { data, error } = await apiClient.PATCH(
    '/v1/partners/{partnerId}/keys/{keyId}/recruitment-origin',
    { params: { path: { partnerId, keyId } }, body: input },
  );
  if (error || !data) return null;
  return data as ApiKey;
}
